<script lang="ts" setup>
import type { Address } from '~/types'

const props = defineProps<{ address?: Address, placeholder?: string, disabled?: boolean }>()
const emit = defineEmits<{
  (e: 'update:address', address: Address): void
  (e: 'showBasicInput'): void
}>()

const emptyAddress: Address = {
  street: '',
  postcode: '',
  city: '',
  country: undefined,
  latitude: undefined,
  longitude: undefined,
}

const { searchtext, suggestions, formatOptionLabel, getAddressById, getAddressByLabel } = useAddressAutoComplete(useAddressString(props.address, { postcode: true, city: true, country: true }))
const selectOptions = computed(() => suggestions.value.map(suggestion => ({
  label: suggestion.label,
  value: suggestion.id,
})))

const isValidInput = computed(() => {
  if (suggestions.value.length === 0 || !searchtext.value) {
    return true
  }
  return !!getAddressByLabel(searchtext.value)
})
const isAddressNotFound = ref(false)
const typingTimeout = ref<NodeJS.Timeout | null>(null)

watch(searchtext, (text) => {
  isAddressNotFound.value = false
  if (!text) {
    emit('update:address', emptyAddress)
  }

  // Clear previous timeout if the user is still typing
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value)
  }

  // Set a new timeout to trigger the not found address button after 1 second
  typingTimeout.value = setTimeout(() => {
    if (text && suggestions.value.length === 0) {
      isAddressNotFound.value = true
    }
  }, 1000)
})
watch(suggestions, () => {
  const selectedAddress = getAddressByLabel(searchtext.value)
  if (selectedAddress) {
    return emit('update:address', {
      ...selectedAddress,
      latitude: `${selectedAddress.latitude}`,
      longitude: `${selectedAddress.longitude}`,
    })
  }
})

function onSelect(id: string) {
  const selectedAddress = getAddressById(id)
  if (!selectedAddress) {
    return emit('update:address', emptyAddress)
  }
  emit('update:address', {
    ...selectedAddress,
    latitude: `${selectedAddress.latitude}`,
    longitude: `${selectedAddress.longitude}`,
  })
}
</script>

<template>
  <n-auto-complete
    v-model:value="searchtext"
    size="large"
    :placeholder="placeholder"
    :disabled="disabled"
    :options="selectOptions"
    :render-label="formatOptionLabel"
    :blur-after-select="true"
    :status="isValidInput ? undefined : 'warning'"
    clearable
    :on-select="onSelect"
  />
  <div class="flex mt-1">
    <n-button
      size="small"
      class="w-full p-6 sm:p-0 rounded-md text-sm whitespace-normal sm:whitespace-nowrap"
      @click="emit('showBasicInput')"
    >
      {{ $t('address.notFound.button') }}
    </n-button>
  </div>
</template>
